import { listen, dispatch } from '@theme/events';
import { SKY_THEME_EVENTS } from '@theme/config';
import {
  HTMLUpdateUtility,
  lockScroll,
  unlockScroll,
  closeDrawer,
  trapFocus,
  removeTrapFocus,
  motionReduced,
  layoutRecalcManager,
  layoutPhaseMeasure,
  layoutPhaseApply,
  copyTextToClipboard,
  setDrawerOpenAccessibility,
  setInteractionDisabled,
} from '@theme/theme-global';
if (!customElements.get('buy-box')) {
  customElements.define(
    'buy-box',
    class BuyBox extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      variantRequestId = 0;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('[name="quantity"]');
      }

      connectedCallback() {
        this.setupHtmlSwapHooks();

        this.onVariantChangeUnsubscriber = listen(
          SKY_THEME_EVENTS.optionValueSelectionChange,
          this.onOptionChange.bind(this),
        );

        this.bindQuantity();
        this.dispatchEvent(new CustomEvent('buy-box:loaded', { bubbles: true }));

        this.bindSwatchDropdowns();

        // variant drawer events
        const closeTriggers = this.querySelectorAll('[is="close-drawer"]');
        closeTriggers.forEach((trigger) => {
          trigger.addEventListener('click', (e) => {
            const drawer = e.target.closest('[data-drawer]');
            closeDrawer(e.target);
            // size-chart-modal manages its own trap (may be nested over quick view)
            if (drawer?.matches?.('size-chart-modal, [data-drawer="size-chart-drawer"]')) return;
            removeTrapFocus();
          });
        });

        this.addEventListener('keyup', (event) => {
          if (event.code.toUpperCase() === 'ESCAPE') {
            // closeDrawer dispatches drawer:close; quick-view restores opener focus there
            closeDrawer(event.target);
            removeTrapFocus();
          }
        });
      }

      beforeHtmlReplace(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      bindSwatchDropdowns() {
        this.addEventListener('click', (event) => {
          const trigger = event.target.closest('button[is="picker-trigger"]');
          if (!trigger || !this.contains(trigger)) return;

          const targetId = trigger.dataset.id || trigger.id;
          if (!targetId) return;

          const dropdown = this.querySelector(`[data-swatch-dropdown-target="${targetId}"]`);
          if (!dropdown) return;
          this.querySelectorAll('[data-swatch-dropdown-target][open]').forEach((openEl) => {
            if (openEl !== dropdown) openEl.removeAttribute('open');
          });
          dropdown.setAttribute('open', '');
          event.stopPropagation();
        });

        this._boundSwatchDropdownDocClick = (event) => {
          const openDropdowns = Array.from(this.querySelectorAll('[data-swatch-dropdown-target][open]'));
          if (!openDropdowns.length) return;
          const clickedInsideDropdown = openDropdowns.some((el) => el.contains(event.target));
          if (clickedInsideDropdown) return;
          const trigger = event.target.closest('button[is="picker-trigger"]');
          if (trigger && this.contains(trigger)) return;
          openDropdowns.forEach((el) => el.removeAttribute('open'));
        };
        document.addEventListener('click', this._boundSwatchDropdownDocClick, true);
      }

      bindQuantity() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('[data-quantity-block="true"]');
        if (!this.quantityForm) return;

        this.applyQuantityLimits();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = listen(SKY_THEME_EVENTS.cartUpdate, this.refreshQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
        document.removeEventListener('click', this._boundSwatchDropdownDocClick, true);
      }

      setupHtmlSwapHooks() {
        this.preProcessHtmlCallbacks.push((html) =>
          html
            .querySelectorAll('.scroll-trigger')
            .forEach((element) => element.classList.add('scroll-trigger--cancel')),
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      onOptionChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetBuyForm();

        const targetProductUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        const productUrl = this.resolveProductUrl(targetProductUrl);
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = !this.urlsMatch(this.dataset.url, targetProductUrl);
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.fetchVariantHtml({
          requestUrl: this.buildVariantRequestUrl(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.swapProductHtml(productUrl, shouldFetchFullPage)
            : this.applyVariantUpdate(productUrl),
        });
      }

      urlsMatch(urlA, urlB) {
        try {
          const a = new URL(urlA, window.location.origin);
          const b = new URL(urlB, window.location.origin);
          return a.pathname === b.pathname;
        } catch {
          return urlA === urlB;
        }
      }

      resolveProductUrl(targetProductUrl) {
        const canonicalUrl = this.dataset.url;

        if (this.urlsMatch(canonicalUrl, targetProductUrl)) {
          return canonicalUrl;
        }

        return targetProductUrl;
      }

      resetBuyForm() {
        this.buyForm?.setSubmitState(true);
        this.buyForm?.setFormError();
        this.stickyBuyForm?.setSubmitState(true);
        this.stickyBuyForm?.setFormError();
      }

      swapProductHtml(productUrl, updateFullPage) {
        return (html) => {
          this.mediaModal?.remove();

          const selector = updateFullPage ? "buy-box[id^='MainProduct']" : 'buy-box';
          const variant = this.readSelectedVariant(html.querySelector(selector));
          this.syncBrowserUrl(productUrl, variant?.id);

          const anchorTop = this.getBoundingClientRect().top;

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('buy-box'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          }

          this.restoreBuyBoxScroll(anchorTop, updateFullPage);
        };
      }

      restoreBuyBoxScroll(anchorTop, updateFullPage) {
        // Overlay/drawer buy boxes live outside main content (e.g. after footer).
        // Scrolling the page to keep that section anchored jumps the viewport to the footer.
        if (this.closest('quick-view-drawer, [data-drawer], .drawer')) return;

        const restore = () => {
          let buyBox = null;

          if (updateFullPage) {
            buyBox = document.querySelector("buy-box[id^='MainProduct']");
          } else if (this.previousElementSibling?.tagName === 'BUY-BOX') {
            // viewTransition inserts the new buy-box before this (old) node
            buyBox = this.previousElementSibling;
          } else if (this.dataset.section) {
            buyBox = document.querySelector(
              `buy-box[data-section="${CSS.escape(this.dataset.section)}"]:not([style*="display: none"])`,
            );
          }

          if (!buyBox || buyBox === this) return;

          const delta = buyBox.getBoundingClientRect().top - anchorTop;
          if (Math.abs(delta) < 1) return;

          if (window.lenis?.scrollTo) {
            window.lenis.scrollTo(window.scrollY + delta, { immediate: true });
          } else {
            window.scrollBy(0, delta);
          }
        };

        requestAnimationFrame(() => requestAnimationFrame(restore));
      }


      fetchVariantHtml({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        const requestId = ++this.variantRequestId;

        this.setOptionPickerUpdating(true);

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            if (requestId !== this.variantRequestId) return;
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            if (requestId !== this.variantRequestId) return;
            // preventScroll: combined listing swaps must not scroll to another
            // matching control (e.g. Featured product) further down the page.
            document.getElementById(targetId)?.focus({ preventScroll: true });
          })
          .catch((error) => {
            if (error.name === 'AbortError') return;
            console.log(error);
          })
          .finally(() => {
            if (requestId === this.variantRequestId) {
              this.setOptionPickerUpdating(false);
            }
          });
      }

      setOptionPickerUpdating(isUpdating) {
        this.querySelectorAll('sky-variant-picker').forEach((el) => {
          el.toggleAttribute('data-updating', isUpdating);
        });
      }

      readSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('sky-variant-picker [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildVariantRequestUrl(url, optionValues, shouldFetchFullPage = false) {
        const requestUrl = new URL(url, window.location.origin);

        if (!shouldFetchFullPage) {
          requestUrl.searchParams.set('section_id', this.sectionId);
        }

        if (optionValues.length) {
          requestUrl.searchParams.set('option_values', optionValues.join(','));
        }

        return `${requestUrl.pathname}${requestUrl.search}`;
      }

      syncOptionPickers(html) {
        const sourceOptionPicker = Array.from(html.querySelectorAll('sky-variant-picker'));
        const destinationOptionPicker = Array.from(this.querySelectorAll('sky-variant-picker'));

        if (!sourceOptionPicker.length || !destinationOptionPicker.length) return;

        const sourceById = new Map(sourceOptionPicker.filter((el) => el.id).map((el) => [el.id, el]));

        destinationOptionPicker.forEach((destinationEl, index) => {
          const sourceEl =
            destinationEl.id && sourceById.has(destinationEl.id)
              ? sourceById.get(destinationEl.id)
              : sourceOptionPicker[index];

          if (!sourceEl) return;

          this.cleanupStaleVariantInputs(destinationEl);

          const destFieldsets = Array.from(destinationEl.querySelectorAll('fieldset.product-variant-input'));
          const srcFieldsets = Array.from(sourceEl.querySelectorAll('fieldset.product-variant-input'));
          this.syncVariantInputNodes(destinationEl, destFieldsets, srcFieldsets);

          const dropdownSelector = '.product-form-input--dropdown, .product-variant-input-dropdown';
          const destDropdowns = Array.from(destinationEl.querySelectorAll(dropdownSelector));
          const srcDropdowns = Array.from(sourceEl.querySelectorAll(dropdownSelector));
          this.syncVariantInputNodes(destinationEl, destDropdowns, srcDropdowns);

          const destVariantJson = destinationEl.querySelector('script[data-selected-variant]');
          const srcVariantJson = sourceEl.querySelector('script[data-selected-variant]');
          if (destVariantJson && srcVariantJson) {
            destVariantJson.innerHTML = srcVariantJson.innerHTML;
          }
        });
      }

      cleanupStaleVariantInputs(container) {
        container
          .querySelectorAll(
            'fieldset.product-variant-input[style*="display: none"], .product-form-input--dropdown[style*="display: none"], .product-variant-input-dropdown[style*="display: none"]',
          )
          .forEach((el) => el.remove());
      }

      syncVariantInputNodes(container, destinationNodes, sourceNodes) {
        while (destinationNodes.length > sourceNodes.length) {
          destinationNodes.pop()?.remove();
        }

        const originalDestCount = destinationNodes.length;

        destinationNodes.forEach((destNode, index) => {
          const srcNode = sourceNodes[index];
          if (!srcNode) {
            destNode.remove();
            return;
          }

          this.preProcessHtmlCallbacks.forEach((callback) => callback(srcNode));
          destNode.replaceWith(srcNode.cloneNode(true));
        });

        if (sourceNodes.length <= originalDestCount) return;

        const insertBefore = container.querySelector('script[data-selected-variant]');
        for (let i = originalDestCount; i < sourceNodes.length; i++) {
          this.preProcessHtmlCallbacks.forEach((callback) => callback(sourceNodes[i]));
          const newNode = sourceNodes[i].cloneNode(true);
          if (insertBefore) {
            container.insertBefore(newNode, insertBefore);
          } else {
            container.appendChild(newNode);
          }
        }
      }

      applyVariantUpdate(productUrl) {
        return (html) => {
          const variant = this.readSelectedVariant(html);

          this.pickupBlock?.update(variant);
          this.syncOptionPickers(html);
          this.syncBrowserUrl(productUrl, variant?.id);
          this.writeVariantId(variant?.id);

          if (!variant) {
            this.showUnavailableState();
            return;
          }

          this.syncMediaGallery(html, variant?.featured_media?.id);

          const replaceBlockHtml = (id, shouldHide = (source) => false) => {
            // console.log(id, this.sectionId, this.dataset.section);

            const source = html.getElementById(`${id}-${this.sectionId}-${this.productId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}-${this.productId}`);

            // console.log(id, source, destination);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          replaceBlockHtml('Price');
          replaceBlockHtml('Badges');
          replaceBlockHtml('Meta');
          replaceBlockHtml('Quantity-Form');
          replaceBlockHtml('Sku', ({ classList }) => classList.contains('hidden'));
          replaceBlockHtml('Inventory', ({ innerText }) => innerText === '');
          replaceBlockHtml('Volume', ({ innerText }) => innerText.trim() === '');
          replaceBlockHtml('Price-Per-Item', ({ classList }) => classList.contains('hidden'));
          replaceBlockHtml('Back-in-Stock-Form');
          replaceBlockHtml('Product-subscription', ({ classList }) => classList.contains('hidden'));
          replaceBlockHtml('Sticky-add-to-cart-price');
          replaceBlockHtml('Sticky-add-to-cart-media');
          replaceBlockHtml('Sticky-add-to-cart-options');

          this.syncQuantityRules(this.sectionId, this.productId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.syncBuyFormState(
            html,
            `product-form-${this.sectionId}-${this.productId}`,
            this.buyForm,
            variant,
          );
          this.syncBuyFormState(
            html,
            `sticky-add-to-cart-product-form-${this.sectionId}-${this.productId}`,
            this.stickyBuyForm,
            variant,
          );

          dispatch(SKY_THEME_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      writeVariantId(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}-${this.productId}, #product-form-installment-${this.dataset.section}-${this.productId}, #sticky-add-to-cart-product-form-${this.dataset.section}-${this.productId}`,
        ).forEach((formEl) => {
          const input = formEl.querySelector('input[name="id"]');
          if (!input) return;
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      syncBuyFormState(html, formId, buyFormDestination, variant) {
        if (!buyFormDestination) return;

        const sourceForm = html.getElementById(formId);
        const buyFormSource = sourceForm?.closest('buy-form');
        if (!sourceForm || !buyFormSource) return;

        const isPreorderItem = buyFormSource.hasAttribute('data-preorder-item');
        buyFormDestination.toggleAttribute('data-preorder-item', isPreorderItem);
        buyFormDestination.isPreorderItem = isPreorderItem;

        const destinationForm = buyFormDestination.querySelector('form[data-type="add-to-cart-form"]');
        if (destinationForm) {
          destinationForm.querySelector('[data-preorder-property="true"]')?.remove();
          const newPreorderInput = sourceForm.querySelector('[data-preorder-property="true"]');
          if (newPreorderInput) destinationForm.appendChild(newPreorderInput.cloneNode(true));

          // Sync visibility when back-in-stock form replaces the ATC form
          destinationForm.classList.toggle('d-none', sourceForm.classList.contains('d-none'));
        }

        const sourceSubmitButton = sourceForm.querySelector('[type="submit"][name="add"]');
        const sourceLabel = sourceSubmitButton?.querySelector('[data-button-label]')?.textContent?.trim();
        const shouldDisable = sourceSubmitButton
          ? sourceSubmitButton.hasAttribute('disabled')
          : variant?.available === false;

        buyFormDestination.setSubmitState(
          shouldDisable,
          shouldDisable ? sourceLabel || window.variantStrings.soldOut : sourceLabel,
        );
      }

      /**
       * When back-in-stock is enabled, unavailable option combos must show the
       * ATC "Unavailable" button and hide the notify form.
       */
      toggleNotifyForm(showBackInStock) {
        const buyForms = [this.buyForm, this.stickyBuyForm].filter(Boolean);

        buyForms.forEach((buyForm) => {
          const bisForm = buyForm.querySelector('form[data-type="back-in-stock-form"]');
          if (!bisForm) return;

          const atcForm = buyForm.querySelector('form[data-type="add-to-cart-form"]');

          if (showBackInStock) {
            atcForm?.classList.add('d-none');
            bisForm.classList.remove('d-none');
            bisForm.classList.add('d-block');
          } else {
            atcForm?.classList.remove('d-none');
            bisForm.classList.remove('d-block');
            bisForm.classList.add('d-none');
          }
        });
      }

      syncBrowserUrl(url, variantId) {
        this.querySelector('[is="share-button-button"]')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`,
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      showUnavailableState() {
        this.buyForm?.setSubmitState(true, window.variantStrings.unavailable);
        this.stickyBuyForm?.setSubmitState(true, window.variantStrings.unavailable);
        this.toggleNotifyForm(false);

        const selectors = [
          'Price',
          'Inventory',
          'Sku',
          'Price-Per-Item',
          'Volume-Note',
          'Volume',
          'Quantity-Rules',
          'Quantity-Form',
          'Sticky-add-to-cart-price',
        ]
          .map((id) => `#${id}-${this.dataset.section}-${this.productId}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => {
          classList.add('hidden');
        });
      }
      syncMediaGallery(html, variantFeaturedMediaId) {
        const mediaGallerySource = this.querySelector('media-gallery');
        const mediaGalleryDestination = html.querySelector('media-gallery');

        if (mediaGallerySource && mediaGalleryDestination) {
          mediaGallerySource.replaceWith(mediaGalleryDestination.cloneNode(true));
        }

        const epcMedia = this.querySelector('[data-epc-media]');
        if (epcMedia) {
          const sourceEpcMedia =
            html.querySelector(`[data-epc-media][data-product-id="${this.productId}"]`) ||
            html.querySelector('[data-epc-media]');
          if (sourceEpcMedia) {
            epcMedia.innerHTML = sourceEpcMedia.innerHTML;
          }
        }
      }
      applyQuantityLimits() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        dispatch(SKY_THEME_EVENTS.quantityUpdate, undefined);
      }

      refreshQuantityRules() {
        const currentVariantId = this.buyForm?.variantInput?.value;
        if (!currentVariantId) return;

        this.querySelector('[data-quantity-rules-cart] [data-loading-spinner]')?.classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.syncQuantityRules(this.dataset.section, this.dataset.productId, html);
          })
          .catch((e) => console.error(e))
          .finally(() =>
            this.querySelector('[data-quantity-rules-cart] [data-loading-spinner]')?.classList.add('hidden'),
          );
      }

      syncQuantityRules(sectionId, productId, html) {
        if (!this.quantityInput) return;
        this.applyQuantityLimits();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}-${productId}`);
        const selectors = ['[name="quantity"]', '[data-quantity-rules="true"]', '[data-quantity-label="true"]'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '[name="quantity"]') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get buyForm() {
        return this.querySelector(`buy-form`);
      }

      get stickyBuyForm() {
        return this.querySelector(`sticky-add-to-cart buy-form`);
      }

      get mediaModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupBlock() {
        return this.querySelector(`pickup-availability`);
      }

      get optionPicker() {
        return this.querySelector('sky-variant-picker');
      }

      get relatedBlock() {
        const relatedBlockSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products',
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedBlockSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get productId() {
        return this.dataset.productId;
      }
    },
  );
}

if (!customElements.get('size-chart-modal')) {
  customElements.define(
    'size-chart-modal',
    class SizeChartModal extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        const closeTriggers = this.querySelectorAll('[is="close-drawer"]');
        closeTriggers.forEach((trigger) => {
          trigger.addEventListener('click', (e) => {
            closeDrawer(e.target);
            removeTrapFocus(this);
            unlockScroll?.();
          });
        });
      }
    },
  );
}

if (!customElements.get('size-chart-trigger')) {
  customElements.define(
    'size-chart-trigger',
    class SizeChartTrigger extends HTMLButtonElement {
      constructor() {
        super();
        this.handleClick = this.handleClick.bind(this);
      }

      connectedCallback() {
        this.addEventListener('click', this.handleClick);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
      }

      handleClick(event) {
        event.preventDefault();
        event.stopPropagation();

        const modal = this.closest('buy-box')?.querySelector('size-chart-modal');
        if (!modal) return;

        setDrawerOpenAccessibility(modal);
        modal.setAttribute('open', '');
        modal.setAttribute('active', '');

        const focusTarget = modal.querySelector('.drawer-close-btn') || modal;
        // push: nest over product/quick-view trap so close returns focus there
        trapFocus(modal, focusTarget, { push: true });
        lockScroll();
      }
    },
    { extends: 'button' },
  );
}

if (!customElements.get('discount-toggle')) {
  customElements.define(
    'discount-toggle',
    class DiscountToggle extends HTMLElement {
      constructor() {
        super();
        this.handleClick = this.handleClick.bind(this);
        this.updateContentHeight = this.updateContentHeight.bind(this);
        this._transitionEndHandler = null;
        this._open = false;
      }

      connectedCallback() {
        this.content = this.querySelector('[data-product-block-discount-content]');
        this.toggle = this.querySelector('button');
        if (!this.content || !this.toggle) return;

        this.toggle.setAttribute('aria-expanded', 'false');
        setInteractionDisabled(this.content, true);
        this.toggle.addEventListener('click', this.handleClick);
        layoutRecalcManager.register(this.updateContentHeight, { root: this });
      }

      disconnectedCallback() {
        this.clearTransition();
        layoutRecalcManager.unregister(this.updateContentHeight);
        this.toggle?.removeEventListener('click', this.handleClick);
      }

      updateContentHeight(phase) {
        if (!this._open || !this.content) return;

        const { content } = this;
        const { height } = content.style;

        if (!height || height === 'auto' || height === '0px') return;

        if (layoutPhaseMeasure(phase)) {
          this._pendingContentHeight = content.scrollHeight;
        }

        if (layoutPhaseApply(phase)) {
          const targetHeight = this._pendingContentHeight;
          if (targetHeight == null) return;

          const currentHeight = parseFloat(content.style.height);
          if (targetHeight !== currentHeight) {
            content.style.height = `${targetHeight}px`;
          }
        }
      }

      clearTransition() {
        if (!this._transitionEndHandler || !this.content) return;
        this.content.removeEventListener('transitionend', this._transitionEndHandler);
        this._transitionEndHandler = null;
      }

      snapHeight() {
        const { content } = this;
        const height = content.offsetHeight;
        content.style.transition = 'none';
        content.style.height = `${height}px`;
      }

      handleClick(event) {
        event.preventDefault();
        event.stopPropagation();
        this._open = !this._open;
        this.transition(this._open);
      }

      onOpenComplete() {
        const { content } = this;
        content.style.height = 'auto';
        content.style.overflow = '';
      }

      applyOpenHeight() {
        const { content } = this;
        const nextHeight = content.scrollHeight;
        content.style.height = `${nextHeight}px`;

        this._transitionEndHandler = (event) => {
          if (event.target !== content || event.propertyName !== 'height') return;
          this.clearTransition();
          if (this._open) this.onOpenComplete();
        };

        content.addEventListener('transitionend', this._transitionEndHandler);
      }

      transition(open) {
        const { content, toggle } = this;

        this.clearTransition();

        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        this.classList.toggle('is-open', open);
        content.classList.toggle('open', open);
        setInteractionDisabled(content, !open);
        content.style.overflow = 'hidden';

        if (motionReduced()) {
          content.style.height = open ? 'auto' : '0px';
          content.style.overflow = open ? '' : 'hidden';
          return;
        }

        this.snapHeight();

        requestAnimationFrame(() => {
          if (this._open !== open) return;
          content.style.transition = '';

          requestAnimationFrame(() => {
            if (this._open !== open) return;

            if (open) {
              this.applyOpenHeight();
              return;
            }

            content.style.height = '0px';

            this._transitionEndHandler = (event) => {
              if (event.target !== content || event.propertyName !== 'height') return;
              this.clearTransition();
              if (!this._open) content.style.overflow = '';
            };

            content.addEventListener('transitionend', this._transitionEndHandler);
          });
        });
      }
    },
  );
}


